import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  CheckSquare, 
  ListTodo, 
  ClipboardList, 
  Plus,
  ArrowRight,
  Megaphone,
  ExternalLink,
  Wallet,
  TrendingUp,
  FileInput,
  FileOutput,
  Briefcase,
  Scale,
  Package,
  CalendarClock
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, hasOpenClawAccess, isAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const checklistProgress = stats?.checklists?.total_items > 0
    ? (stats.checklists.completed_items / stats.checklists.total_items) * 100
    : 0;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard">
      {/* Welcome Section */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-[Manrope]">
          {t('welcome')}, {user?.name?.split(' ')[0]}!
        </h2>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">
          {format(new Date(), 'EEEE, d. MMMM yyyy', { locale: de })}
        </p>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Personal Tasks Card */}
        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">
              {t('personalTasks')}
            </CardTitle>
            <CheckSquare className="h-4 w-4 sm:h-5 sm:w-5 text-[#D92632]" />
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
              {stats?.personal_tasks?.total || 0}
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {stats?.personal_tasks?.completed || 0} {t('completed').toLowerCase()}
            </p>
          </CardContent>
        </Card>

        {/* Assigned Tasks Card */}
        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">
              {t('assignedTasks')}
            </CardTitle>
            <ListTodo className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
              {stats?.assigned_tasks?.pending || 0}
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {t('pendingTasks').toLowerCase()}
            </p>
          </CardContent>
        </Card>

        {/* Checklist Progress Card */}
        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">
              {t('checklistProgress')}
            </CardTitle>
            <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
              {Math.round(checklistProgress)}%
            </div>
            <Progress value={checklistProgress} className="mt-2 h-1.5 sm:h-2" />
          </CardContent>
        </Card>

        {/* Notifications Card */}
        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">
              {t('notifications')}
            </CardTitle>
            <Megaphone className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
              {stats?.unread_notifications || 0}
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              ungelesen
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Recent Updates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Quick Actions */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-base sm:text-lg font-semibold text-slate-900 font-[Manrope]">
              {t('quickActions')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0 space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start border-slate-200 hover:bg-slate-50 hover:border-[#D92632] hover:text-[#D92632] h-10 sm:h-auto text-sm"
              onClick={() => navigate('/tasks')}
              data-testid="quick-new-task"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('newTask')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start border-slate-200 hover:bg-slate-50 hover:border-[#D92632] hover:text-[#D92632] h-10 sm:h-auto text-sm"
              onClick={() => navigate('/checklists')}
              data-testid="quick-new-checklist"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('newChecklist')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start border-slate-200 hover:bg-slate-50 hover:border-[#D92632] hover:text-[#D92632] h-10 sm:h-auto text-sm"
              onClick={() => navigate('/contacts')}
              data-testid="quick-new-contact"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('newContact')}
            </Button>
          </CardContent>
        </Card>

        {/* Recent Updates */}
        <Card className="bg-white border border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6">
            <CardTitle className="text-base sm:text-lg font-semibold text-slate-900 font-[Manrope]">
              {t('recentUpdates')}
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-[#D92632] hover:text-[#B91C26] hover:bg-red-50 text-xs sm:text-sm"
              onClick={() => navigate('/updates')}
            >
              {t('edit')} <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            {stats?.recent_updates?.length > 0 ? (
              <div className="space-y-3 sm:space-y-4">
                {stats.recent_updates.map((update) => (
                  <div 
                    key={update.id} 
                    className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#D92632]/10 flex items-center justify-center flex-shrink-0">
                      <Megaphone className="h-4 w-4 sm:h-5 sm:w-5 text-[#D92632]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate text-sm sm:text-base">{update.title}</p>
                      <p className="text-xs sm:text-sm text-slate-500 line-clamp-2 mt-0.5">
                        {update.content}
                      </p>
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-1">
                        {update.created_by_name} • {format(new Date(update.created_at), 'dd.MM.yyyy')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-6 sm:py-8 text-sm">{t('noData')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* External Systems */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="text-base sm:text-lg font-semibold text-slate-900 font-[Manrope]">
            {t('externalSystems')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-4">
            <a
              href="https://salary.kaufmanns-consulting.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-[#D92632] hover:bg-red-50 transition-all group"
              data-testid="external-payroll"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#D92632]/10 flex items-center justify-center group-hover:bg-[#D92632]/20 transition-colors">
                <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-[#D92632]" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-[#D92632] text-center">
                {t('payroll')}
              </span>
            </a>
            
            <a
              href="https://sales.kaufmanns-consulting.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all group"
              data-testid="external-sales"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-blue-600 text-center">
                {t('salesCRM')}
              </span>
            </a>
            
            <a
              href="https://accounting.kaufmanns-consulting.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all group"
              data-testid="external-accounting-in"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <FileInput className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-emerald-600 text-center leading-tight">
                {t('invoicesIncoming')}
              </span>
            </a>
            
            <a
              href="https://accounting1.kaufmanns-consulting.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all group"
              data-testid="external-accounting-out"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                <FileOutput className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-amber-600 text-center leading-tight">
                {t('invoicesOutgoing')}
              </span>
            </a>
            
            <a
              href="https://jobs-kaufmanns-consulting.com/admin/users/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-all group"
              data-testid="external-job-portal"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-purple-600 text-center">
                {t('jobPortal')}
              </span>
            </a>
            
            <a
              href="https://inventar.kaufmanns-consulting.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-orange-500 hover:bg-orange-50 transition-all group"
              data-testid="external-inventory"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <Package className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-orange-600 text-center">
                {t('inventory')}
              </span>
            </a>
            
            {hasOpenClawAccess && (
              <a
                href="https://openclaw.kaufmanns-consulting.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-cyan-500 hover:bg-cyan-50 transition-all group"
                data-testid="external-openclaw"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-cyan-100 flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                  <Scale className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-cyan-600 text-center">
                  {t('openClaw')}
                </span>
              </a>
            )}
            
            {isAdmin && (
              <a
                href="https://docs.google.com/spreadsheets/d/1l4CTXVm3KDZ1SSeTDO9oEUArmUplUq7CC92SeI6bEng/edit?gid=397451541#gid=397451541"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all group"
                data-testid="external-project-mc"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                  <CalendarClock className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-indigo-600 text-center leading-tight">
                  {t('projectMCSchedule')}
                </span>
              </a>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
